/**
 * API base URL.
 *
 * Resolution order:
 *   1. VITE_API_URL (explicit override — e.g. a separate API host).
 *   2. Same-origin `/api` in the browser (Option A): the web server reverse-
 *      proxies `/api/*` to the API with the tenant Host preserved, so requests
 *      from `demo-birthday.events.ejthub.com/api/...` resolve to that tenant.
 *   3. `http://localhost:3000` as a last resort (dev/SSR with no window).
 */
export const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:3000');

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

let getAccessToken: (() => string | null) | null = null;

export function setTokenGetter(getter: () => string | null) {
  getAccessToken = getter;
}

/** Get the current access token (for manual fetch calls like file uploads) */
export function getCurrentToken(): string | null {
  return getAccessToken?.() || null;
}

// ── Tenant selection ──────────────────────────────────────────────────────
// When the API and web app are on separate fixed hosts (e.g. Railway), the
// browser's host can't carry the tenant to the API. Instead we select the
// tenant explicitly: a `?tenant=<slug>` query param is captured once and sent
// as an `X-Tenant-Slug` header on every API call. The platform/operator
// console never sets this, so it stays platform-scoped.
const TENANT_STORAGE_KEY = 'ep_tenant_slug';

/** Persist the active tenant slug (empty/undefined clears it). */
export function setTenantSlug(slug: string | null | undefined): void {
  try {
    if (slug && slug.trim()) {
      sessionStorage.setItem(TENANT_STORAGE_KEY, slug.trim().toLowerCase());
    } else {
      sessionStorage.removeItem(TENANT_STORAGE_KEY);
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/** The active tenant slug, or null for platform scope. */
export function getTenantSlug(): string | null {
  try {
    return sessionStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Capture a `?tenant=` query param (if present) into session storage. Call once
 * on app start, before any API calls. Returns the active slug (new or existing).
 */
export function initTenantFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('tenant');
    if (fromUrl) setTenantSlug(fromUrl);
  } catch {
    /* no window / bad URL — ignore */
  }
  return getTenantSlug();
}

/** Attach the X-Tenant-Slug header to a headers object when a tenant is active. */
function applyTenantHeader(headers: Record<string, string>): void {
  const slug = getTenantSlug();
  if (slug) headers['X-Tenant-Slug'] = slug;
}

// ── Event selection ───────────────────────────────────────────────────────
// A tenant (event planner) owns many independent events. Once the operator
// picks an event to work on, we persist its id and send it as an `X-Event-Id`
// header on every API call. The server's event middleware then auto-scopes all
// operational reads/writes (guests, tables, zones, orders, tickets, tiers) to
// that event. When no event is selected the header is omitted and the server
// falls back to single-event behavior.
const EVENT_STORAGE_KEY = 'ep_event_id';

/** Persist the active event id (empty/undefined clears it). */
export function setEventId(eventId: string | null | undefined): void {
  try {
    if (eventId && eventId.trim()) {
      sessionStorage.setItem(EVENT_STORAGE_KEY, eventId.trim());
    } else {
      sessionStorage.removeItem(EVENT_STORAGE_KEY);
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/** The active event id, or null if none selected. */
export function getEventId(): string | null {
  try {
    return sessionStorage.getItem(EVENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Capture an `?event=` query param (if present) into session storage. Call once
 * on app start, after initTenantFromUrl. Returns the active event id (new or
 * existing).
 */
export function initEventFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('event');
    if (fromUrl) setEventId(fromUrl);
  } catch {
    /* no window / bad URL — ignore */
  }
  return getEventId();
}

/** Attach the X-Event-Id header to a headers object when an event is selected. */
function applyEventHeader(headers: Record<string, string>): void {
  const eventId = getEventId();
  if (eventId) headers['X-Event-Id'] = eventId;
}

export async function apiClient<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(customHeaders as Record<string, string>),
  };

  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  applyTenantHeader(headers);
  applyEventHeader(headers);

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Upload a file as multipart/form-data with a single file field.
 * Used for file-only upload flows (e.g., order import pre-processing).
 */
export async function uploadFile<T = unknown>(
  path: string,
  file: File,
  fieldName: string = 'file',
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  const headers: Record<string, string> = {};
  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  applyTenantHeader(headers);
  applyEventHeader(headers);

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  return response.json() as Promise<T>;
}

/**
 * Submit a multipart/form-data request with optional file attachment.
 * Used for booklet submission create/edit flows that include file uploads.
 */
export async function uploadSubmission<T = unknown>(
  path: string,
  method: 'POST' | 'PATCH',
  fields: Record<string, string>,
  file?: File,
): Promise<T> {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value != null && value !== '') {
      formData.append(key, value);
    }
  });

  if (file) {
    formData.append('file', file);
  }

  const headers: Record<string, string> = {};
  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  applyTenantHeader(headers);
  applyEventHeader(headers);

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  return response.json() as Promise<T>;
}

/**
 * True when an API error is the backend's "you must select an event first"
 * signal (HTTP 400 with `eventSelectionRequired: true`). Raised by the
 * EventSelectionRequiredGuard on operational reads when a tenant has multiple
 * events and none is selected. Pages can use this to prompt the operator to
 * pick an event (via the Active event picker) rather than show a generic error.
 *
 * The backend already supplies a friendly `message`, so pages that render
 * `err.message` get a sensible string for free; this helper is for cases that
 * want to branch the UI (e.g. open the event switcher).
 */
export function isEventSelectionError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { eventSelectionRequired?: boolean }).eventSelectionRequired === true
  );
}
